/** The prices of a tariff (energy charge, fixed charges, tax, exports): one editor for the admin rate tables and
 * installers' own prices. Values may be left empty while editing; `priceProblems` lists what blocks saving. */
import { Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import type { S } from "../api/client";
import { useT, type Translate } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import { translateOutside } from "../i18n/problems";
import { priceProblems } from "../lib/tariffChecks";
import { Button, Field, NumberInput, Segmented, Select, cx } from "./ui";

export { priceProblems };

export type Tariff = S["Tariff"];
type Mode = "flat" | "tiered" | "tou" | "schedule";

const EXPORT_MODES: { value: S["ExportMode"]; label: MessageKey }[] = [
  { value: "net_metering", label: "rules.prices.exportMode.netMetering" },
  { value: "net_billing", label: "rules.prices.exportMode.netBilling" },
  { value: "feed_in", label: "rules.prices.exportMode.feedIn" },
  { value: "gross", label: "rules.prices.exportMode.gross" },
];
const HOURS = Array.from({ length: 25 }, (_, h) => ({ value: String(h), label: `${String(h).padStart(2, "0")}:00` }));

export const CURRENCIES = ["USD", "CAD", "GBP", "EUR", "AUD", "MXN"];

/** What the summaries need to speak the reader's language: `t` and a number formatter (`usePriceText()` in a
 * component). Without one they use the provider's registered language and the page's locale. */
export interface PriceText {
  t: Translate;
  number: (value: number, minDigits: number, maxDigits: number) => string;
}

export function usePriceText(): PriceText {
  const t = useT();
  const intl = useIntl();
  return useMemo(() => ({ t, number: (v, min, max) => intl.formatNumber(v, { minimumFractionDigits: min, maximumFractionDigits: max }) }), [t, intl]);
}

const OUTSIDE: PriceText = {
  t: (key, values) => translateOutside(key, values) ?? key,
  number: (v, min, max) => new Intl.NumberFormat(document.documentElement.lang || undefined, { minimumFractionDigits: min, maximumFractionDigits: max }).format(v),
};

/** Short month names in the reader's language (January = index 0). */
function useMonths(): string[] {
  const intl = useIntl();
  return useMemo(() => Array.from({ length: 12 }, (_, m) => intl.formatDate(Date.UTC(2001, m, 15), { month: "short", timeZone: "UTC" })), [intl]);
}

/** An empty tariff to fill in: no values are prefilled (nothing of Solar OS's prices reaches installers). */
export function blankTariff(name: string, currency: string): Tariff {
  return {
    name,
    currency,
    energy: { type: "flat", rate: null },
    fixed_monthly: 0,
    tax_rate: 0,
    export: { mode: "net_metering", carry_forward: true, true_up_month: 12, surplus_rate: 0 },
  };
}

// ------------------------------------------------------------- summaries
/** A price per kWh in the currency's minor unit (¢ or p), isolated left to right (U+2066…U+2069) so the unit stays
 * after its number inside right-to-left text. */
export function perKwh(rate: number | null | undefined, currency: string, f: PriceText = OUTSIDE): string {
  if (rate == null) return "—";
  const minor = currency === "GBP" ? "p" : "¢";
  return `\u2066${f.number(rate * 100, 1, 2)}${minor}\u2069`;
}

function rates(t: Tariff): number[] {
  const e = t.energy;
  if (e.type === "flat") return [e.rate ?? 0];
  if (e.type === "tiered") return (e.tiers ?? []).map((x) => x.rate);
  if (e.type === "tou") return (e.seasons ?? []).flatMap((s) => [s.base_rate, ...s.periods.map((p) => p.rate)]);
  return (e.periods ?? []).flatMap((p) => p.tiers.map((x) => x.rate));
}

export function energySummary(t: Tariff, f: PriceText = OUTSIDE): string {
  const values = rates(t);
  const low = Math.min(...values);
  const high = Math.max(...values);
  const price = low === high ? perKwh(low, t.currency, f) : f.t("rules.prices.summary.span", { low: perKwh(low, t.currency, f), high: perKwh(high, t.currency, f) });
  const e = t.energy;
  if (e.type === "flat") return f.t("rules.prices.summary.flat", { price });
  if (e.type === "tiered") return f.t("rules.prices.summary.tiered", { count: e.tiers?.length ?? 0, price });
  if (e.type === "tou") return f.t("rules.prices.summary.tou", { price });
  const tiered = (e.periods ?? []).some((p) => p.tiers.length > 1);
  return f.t("rules.prices.summary.schedule", { count: e.periods?.length ?? 0, tiered: tiered ? "yes" : "no", price });
}

export function fixedSummary(t: Tariff, f: PriceText = OUTSIDE): string {
  const parts = [];
  if (t.fixed_monthly) parts.push(f.t("rules.prices.summary.monthly", { amount: f.number(t.fixed_monthly, 2, 2) }));
  if (t.fixed_daily) parts.push(f.t("rules.prices.summary.daily", { price: perKwh(t.fixed_daily, t.currency, f) }));
  return parts.length ? parts.join(" + ") : "—";
}

export function exportSummary(t: Tariff, f: PriceText = OUTSIDE): string {
  const x = t.export;
  if (x.mode === "net_metering")
    return x.credit_expiry_months ? f.t("rules.prices.summary.netMeteringExpiry", { months: x.credit_expiry_months }) : f.t("rules.prices.summary.netMetering");
  const rate = x.rate_schedule ? "hourly" : x.rate == null ? "byPeriod" : "rate";
  return f.t("rules.prices.summary.export", {
    mode: x.mode === "net_billing" ? "netBilling" : x.mode === "feed_in" ? "feedIn" : "gross",
    rate,
    price: x.rate == null ? "" : perKwh(x.rate, t.currency, f),
  });
}


function modeOf(t: Tariff): Mode {
  return t.energy.type;
}

/** The names are the company's data once saved: they start in the reader's language. */
function touTemplate(t: Translate): S["TariffEnergy"] {
  return {
    type: "tou",
    seasons: [
      {
        name: t("rules.prices.template.allYear"),
        months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        base_rate: null as unknown as number,
        periods: [{ name: t("rules.prices.template.peak"), hours: [{ start: 16, end: 21 }], rate: null as unknown as number }],
      },
    ],
  };
}

export function PriceFields({ value: t, onChange, showErrors, allowSchedule }: {
  value: Tariff;
  onChange: (t: Tariff) => void;
  /** Show problems (after a save attempt); fields touched since are re-checked live. */
  showErrors?: boolean;
  /** Admin only: URDB-style schedules as JSON. */
  allowSchedule?: boolean;
}) {
  const tr = useT();
  const text = usePriceText();
  const months = useMonths();
  const raw = showErrors ? priceProblems(t) : {};
  const problems: Record<string, string> = Object.fromEntries(Object.entries(raw).map(([k, v]) => [k, tr(v)]));
  const set = (patch: Partial<Tariff>) => onChange({ ...t, ...patch });
  const setEnergy = (energy: S["TariffEnergy"]) => set({ energy });
  const setExport = (patch: Partial<S["ExportCredit"]>) => set({ export: { ...t.export, ...patch } });
  const [json, setJson] = useState(() => JSON.stringify(t.energy, null, 1));
  const [jsonError, setJsonError] = useState<string | null>(null);
  const unit = `${t.currency}/kWh`;
  const mode = modeOf(t);
  const modes: { value: Mode; label: string }[] = [
    { value: "flat", label: tr("rules.prices.mode.flat") },
    { value: "tiered", label: tr("rules.prices.mode.tiered") },
    { value: "tou", label: tr("rules.prices.mode.tou") },
  ];
  if (allowSchedule || mode === "schedule") modes.push({ value: "schedule", label: tr("rules.prices.mode.schedule") });

  const switchMode = (m: Mode) => {
    if (m === mode) return;
    if (m === "flat") setEnergy({ type: "flat", rate: null });
    if (m === "tiered") setEnergy({ type: "tiered", tiers: [{ up_to_kwh: 500, rate: null as unknown as number }, { up_to_kwh: null, rate: null as unknown as number }] });
    if (m === "tou") setEnergy(touTemplate(tr));
    if (m === "schedule") {
      const zeros = Array.from({ length: 12 }, () => Array.from({ length: 24 }, () => 0));
      const energy: S["TariffEnergy"] = { type: "schedule", periods: [{ name: tr("rules.prices.template.allHours"), tiers: [{ up_to_kwh: null, rate: 0 }] }], weekday_schedule: zeros, weekend_schedule: zeros };
      setJson(JSON.stringify(energy, null, 1));
      setEnergy(energy);
    }
  };

  const tiers = t.energy.type === "tiered" ? t.energy.tiers ?? [] : [];
  const setTiers = (next: S["TariffTier"][]) => setEnergy({ type: "tiered", tiers: next });
  const seasons = t.energy.type === "tou" ? t.energy.seasons ?? [] : [];
  const setSeasons = (next: S["TouSeason"][]) => setEnergy({ type: "tou", seasons: next });
  const setSeason = (i: number, patch: Partial<S["TouSeason"]>) => setSeasons(seasons.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  return (
    <div className="stack price-fields">
      <Field label={tr("rules.prices.energyCharge")}>
        <Segmented<Mode> value={mode} onChange={switchMode} options={modes} />
      </Field>

      {t.energy.type === "flat" && (
        <Field label={tr("rules.prices.pricePerKwh")} error={problems.rate}>
          <NumberInput value={t.energy.rate ?? null} step={0.0001} min={0} placeholder={tr("rules.prices.pricePerKwhExample", { example: text.number(0.183, 3, 3) })} suffix={unit} onChange={(v) => setEnergy({ type: "flat", rate: v })} />
        </Field>
      )}

      {t.energy.type === "tiered" && (
        <div className="stack" style={{ gap: 10 }}>
          {tiers.map((tier, i) => (
            <div key={i} className="form-grid tier-row">
              <Field label={i === 0 ? tr("rules.prices.tierEnd") : undefined} error={problems[`tierEnd${i}`]}>
                {i === tiers.length - 1 ? (
                  <div className="input input-static">{tr("rules.prices.everythingAbove")}</div>
                ) : (
                  <NumberInput value={tier.up_to_kwh} min={0} suffix="kWh" onChange={(v) => setTiers(tiers.map((x, j) => (j === i ? { ...x, up_to_kwh: v } : x)))} />
                )}
              </Field>
              <Field label={i === 0 ? tr("rules.prices.price") : undefined} error={problems[`tier${i}`]}>
                <NumberInput value={tier.rate} step={0.0001} min={0} suffix={unit} onChange={(v) => setTiers(tiers.map((x, j) => (j === i ? { ...x, rate: v as number } : x)))} />
              </Field>
            </div>
          ))}
          <div className="row">
            <Button size="sm" icon={<Plus />} onClick={() => setTiers([...tiers.slice(0, -1), { ...tiers[tiers.length - 1], up_to_kwh: (tiers[tiers.length - 2]?.up_to_kwh ?? 0) + 500 }, { up_to_kwh: null, rate: null as unknown as number }])}>
              {tr("rules.prices.addTier")}
            </Button>
            {tiers.length > 2 && (
              <Button size="sm" variant="ghost" onClick={() => setTiers(tiers.slice(0, -1).map((x, j, a) => (j === a.length - 1 ? { ...x, up_to_kwh: null } : x)))}>
                {tr("rules.prices.removeTier")}
              </Button>
            )}
          </div>
        </div>
      )}

      {t.energy.type === "tou" && (
        <div className="stack" style={{ gap: 12 }}>
          {problems.months && <span className="field-error">{problems.months}</span>}
          {seasons.map((season, i) => (
            <div key={i} className="tou-season">
              <div className="row" style={{ justifyContent: "space-between", gap: 8 }}>
                <input dir="auto" className="input season-name" aria-label={tr("rules.prices.seasonName")} value={season.name} onChange={(e) => setSeason(i, { name: e.target.value })} />
                {seasons.length > 1 && (
                  <button className="icon-btn" aria-label={tr("rules.prices.removeSeason")} onClick={() => setSeasons(seasons.filter((_, j) => j !== i))}>
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
              <div className="month-picks" role="group" aria-label={tr("rules.prices.months")}>
                {months.map((m, k) => {
                  const on = season.months.includes(k + 1);
                  return (
                    <button
                      key={m}
                      type="button"
                      className={cx("month-pick", on && "on")}
                      aria-pressed={on}
                      onClick={() =>
                        setSeasons(
                          seasons.map((s, j) =>
                            j === i
                              ? { ...s, months: on ? s.months.filter((x) => x !== k + 1) : [...s.months, k + 1].sort((a, b) => a - b) }
                              : { ...s, months: on ? s.months : s.months.filter((x) => x !== k + 1) },
                          ),
                        )
                      }
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
              {season.periods.length > 0 && (
                <div className="tou-grid">
                  <span className="tou-head">{tr("rules.prices.period")}</span>
                  <span className="tou-head">{tr("rules.prices.from")}</span>
                  <span className="tou-head">{tr("rules.prices.to")}</span>
                  <span className="tou-head">{tr("rules.prices.price")}</span>
                  <span />
                  {season.periods.map((period, j) => {
                    const setPeriod = (patch: Partial<S["TouPeriod"]>) => setSeason(i, { periods: season.periods.map((p, k) => (k === j ? { ...p, ...patch } : p)) });
                    const hours = period.hours[0] ?? { start: 16, end: 21 };
                    const error = problems[`hours${i}.${j}`] ?? problems[`period${i}.${j}`];
                    return (
                      <div key={j} className="tou-row">
                        <input dir="auto" className="input" aria-label={tr("rules.prices.periodName")} value={period.name} onChange={(e) => setPeriod({ name: e.target.value })} />
                        <Select ariaLabel={tr("rules.prices.from")} value={String(hours.start)} options={HOURS.slice(0, 24)} onChange={(v) => setPeriod({ hours: [{ start: Number(v), end: hours.end }] })} />
                        <Select ariaLabel={tr("rules.prices.to")} value={String(hours.end)} options={HOURS.slice(1)} onChange={(v) => setPeriod({ hours: [{ start: hours.start, end: Number(v) }] })} />
                        <NumberInput value={period.rate} step={0.0001} min={0} suffix={unit} onChange={(v) => setPeriod({ rate: v as number })} />
                        <button className="icon-btn" aria-label={tr("rules.prices.removePeriod")} onClick={() => setSeason(i, { periods: season.periods.filter((_, k) => k !== j) })}>
                          <Trash2 size={15} />
                        </button>
                        {error && <span className="field-error tou-error">{error}</span>}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="form-grid">
                <Field label={tr("rules.prices.otherHours")} error={problems[`base${i}`]}>
                  <NumberInput value={season.base_rate} step={0.0001} min={0} suffix={unit} onChange={(v) => setSeason(i, { base_rate: v as number })} />
                </Field>
              </div>
              <div className="row">
                <Button size="sm" icon={<Plus />} onClick={() => setSeason(i, { periods: [...season.periods, { name: season.periods.length ? tr("rules.prices.template.midPeak") : tr("rules.prices.template.peak"), hours: [{ start: 7, end: 11 }], rate: null as unknown as number }] })}>
                  {tr("rules.prices.addPeriod")}
                </Button>
              </div>
            </div>
          ))}
          <div className="row">
            <Button size="sm" variant="ghost" icon={<Plus />} onClick={() => setSeasons([...seasons, { name: tr("rules.prices.template.season", { n: seasons.length + 1 }), months: [], base_rate: null as unknown as number, periods: [] }])}>
              {tr("rules.prices.addSeason")}
            </Button>
          </div>
        </div>
      )}

      {t.energy.type === "schedule" && (
        <Field label={tr("rules.prices.json.label")} error={jsonError} hint={tr("rules.prices.json.hint")}>
          <textarea
            className="textarea code"
            style={{ minHeight: 220 }}
            value={json}
            onChange={(e) => {
              setJson(e.target.value);
              try {
                setEnergy(JSON.parse(e.target.value) as S["TariffEnergy"]);
                setJsonError(null);
              } catch {
                setJsonError(tr("rules.prices.json.invalid"));
              }
            }}
          />
        </Field>
      )}

      <div className="form-grid-3">
        <Field label={tr("rules.prices.fixed")}>
          <NumberInput value={t.fixed_monthly} min={0} suffix={tr("rules.prices.perMonthIn", { currency: t.currency })} onChange={(v) => set({ fixed_monthly: v ?? 0 })} />
        </Field>
        <Field label={tr("rules.prices.daily")}>
          <NumberInput value={t.fixed_daily ?? null} min={0} step={0.0001} placeholder={tr("rules.prices.none")} suffix={tr("rules.prices.perDay")} onChange={(v) => set({ fixed_daily: v })} />
        </Field>
        <Field label={tr("rules.prices.minimum")}>
          <NumberInput value={t.minimum_monthly ?? null} min={0} placeholder={tr("rules.prices.none")} suffix={tr("rules.prices.perMonth")} onChange={(v) => set({ minimum_monthly: v })} />
        </Field>
        <Field label={tr("rules.prices.tax")}>
          <NumberInput value={t.tax_rate == null ? 0 : Math.round(t.tax_rate * 100000) / 1000} min={0} max={100} suffix="%" onChange={(v) => set({ tax_rate: (v ?? 0) / 100 })} />
        </Field>
        <Field label={tr("rules.prices.exports")} className="span-2-of-3">
          <Select value={t.export.mode} onChange={(v) => setExport({ mode: v })} options={EXPORT_MODES.map((m) => ({ value: m.value, label: tr(m.label) }))} />
        </Field>
      </div>
      <div className="form-grid-3">
        {t.export.mode !== "net_metering" ? (
          <Field label={tr("rules.prices.exportPrice")} error={problems.exportRate}>
            <NumberInput value={t.export.rate ?? null} min={0} step={0.0001} suffix={unit} onChange={(v) => setExport({ rate: v })} />
          </Field>
        ) : (
          <Field label={tr("rules.prices.creditsLast.label")} hint={tr("rules.prices.creditsLast.hint")}>
            <NumberInput value={t.export.credit_expiry_months ?? null} min={1} max={120} step={1} placeholder={tr("rules.prices.creditsLast.placeholder")} suffix={tr("rules.unit.months")} onChange={(v) => setExport({ credit_expiry_months: v == null ? null : Math.round(v) })} />
          </Field>
        )}
        <Field label={tr("rules.prices.trueUp.label")} hint={tr("rules.prices.trueUp.hint")}>
          <Select value={String(t.export.true_up_month ?? 12)} options={months.map((m, i) => ({ value: String(i + 1), label: m }))} onChange={(v) => setExport({ true_up_month: Number(v) })} />
        </Field>
        <Field label={tr("rules.prices.surplus.label")} hint={tr("rules.prices.surplus.hint")}>
          <NumberInput value={t.export.surplus_rate ?? 0} min={0} step={0.0001} suffix={unit} onChange={(v) => setExport({ surplus_rate: v ?? 0 })} />
        </Field>
      </div>
    </div>
  );
}
